/* ═══════════════════════════════════════════════════
   STUDENT DASHBOARD — Firebase Connected
   File: src/components/Dashboard/Dashboard.js
   
   Shows: profile, latest results, test history, saved careers
   Loads all data from Firestore
═══════════════════════════════════════════════════ */

import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import {
  getUserProfile,
  getTestHistory,
  logoutUser,
} from "../../utils/firebase";

const CSS = `
  .pf-dash { min-height: 100vh; padding: 110px 0 80px; position: relative; }
  .pf-dash-wrap { max-width: 1000px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 1; }

  /* Welcome header */
  .pf-dash-welcome {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 32px; flex-wrap: wrap; gap: 16px;
  }
  .pf-dash-hello { font-family: 'DM Serif Display', serif; font-size: 1.6em; color: #F0F0F5; }
  .pf-dash-hello em { font-style: italic; background: linear-gradient(135deg, #FF6B35, #FFD23F); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .pf-dash-logout {
    padding: 8px 20px; border-radius: 50px; border: 1.5px solid rgba(255,77,106,0.2);
    background: rgba(255,77,106,0.05); color: #FF4D6A; font-weight: 600; font-size: 0.82em;
    cursor: pointer; transition: all 0.3s ease; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .pf-dash-logout:hover { background: rgba(255,77,106,0.12); border-color: rgba(255,77,106,0.4); }

  .pf-dash-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  .pf-dash-card {
    padding: 28px; border-radius: 20px; background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.05); transition: all 0.3s ease;
    animation: fadeInUp 0.6s ease both;
  }
  .pf-dash-card:hover { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.08); }
  .pf-dash-card.full { grid-column: 1 / -1; }
  .pf-dash-card-h { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
  .pf-dash-card-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2em; }
  .pf-dash-card-title { font-weight: 800; font-size: 0.95em; color: #F0F0F5; }

  /* Profile stats */
  .pf-dash-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .pf-dash-stat-item { padding: 14px; border-radius: 12px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.03); text-align: center; }
  .pf-dash-stat-val { font-family: 'DM Serif Display', serif; font-size: 1.5em; display: block; margin-bottom: 2px; }
  .pf-dash-stat-label { font-size: 0.68em; color: rgba(240,240,245,0.3); font-weight: 600; }

  /* Personality */
  .pf-dash-persona { display: flex; align-items: center; gap: 14px; padding: 16px; border-radius: 14px; background: rgba(255,107,53,0.04); border: 1px solid rgba(255,107,53,0.1); margin-bottom: 14px; }
  .pf-dash-persona-emoji { font-size: 2em; }
  .pf-dash-persona-title { font-weight: 700; font-size: 0.95em; color: #F0F0F5; }
  .pf-dash-persona-sub { font-size: 0.78em; color: rgba(240,240,245,0.4); margin-top: 2px; }

  /* Trait mini bars */
  .pf-dash-traits { display: flex; flex-direction: column; gap: 6px; }
  .pf-dash-trait { display: flex; align-items: center; gap: 8px; }
  .pf-dash-trait-label { width: 70px; font-size: 0.7em; font-weight: 600; color: rgba(240,240,245,0.35); }
  .pf-dash-trait-bar { flex: 1; height: 6px; border-radius: 6px; background: rgba(255,255,255,0.04); overflow: hidden; }
  .pf-dash-trait-fill { height: 100%; border-radius: 6px; }
  .pf-dash-trait-pct { width: 30px; font-size: 0.68em; font-weight: 600; text-align: right; font-family: 'JetBrains Mono', monospace; }

  /* Top careers */
  .pf-dash-career-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.03); font-size: 0.86em; }
  .pf-dash-career-row:last-child { border-bottom: none; }
  .pf-dash-career-emoji { font-size: 1.2em; }
  .pf-dash-career-name { flex: 1; color: rgba(240,240,245,0.6); font-weight: 600; }
  .pf-dash-career-pct { font-family: 'JetBrains Mono', monospace; font-size: 0.8em; font-weight: 600; }

  /* History */
  .pf-dash-history-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 12px; background: rgba(255,255,255,0.015); border: 1px solid rgba(255,255,255,0.03); margin-bottom: 8px; }
  .pf-dash-history-emoji { font-size: 1.4em; }
  .pf-dash-history-title { font-weight: 700; font-size: 0.88em; color: #F0F0F5; }
  .pf-dash-history-date { font-size: 0.72em; color: rgba(240,240,245,0.25); margin-top: 2px; }
  .pf-dash-history-careers { font-size: 0.72em; color: rgba(240,240,245,0.35); margin-top: 2px; }

  /* Empty */
  .pf-dash-empty { text-align: center; padding: 24px 0; font-size: 0.85em; color: rgba(240,240,245,0.25); }
  .pf-dash-empty-icon { font-size: 2em; margin-bottom: 8px; display: block; opacity: 0.4; }

  /* Loading */
  .pf-dash-loading { text-align: center; padding: 40px 0; }
  .pf-dash-spinner { width: 40px; height: 40px; border: 3px solid rgba(255,255,255,0.06); border-top-color: #FF6B35; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeInUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
  @media(max-width: 768px) { .pf-dash-grid { grid-template-columns: 1fr; } .pf-dash-welcome { flex-direction: column; text-align: center; } }
`;

const TRAIT_COLORS = {
  analytical: "#6C5CE7",
  creative: "#A29BFE",
  social: "#00D4AA",
  technical: "#00B894",
  business: "#FDCB6E",
};
const TRAIT_LABELS = {
  analytical: "Analytical",
  creative: "Creative",
  social: "Social",
  technical: "Technical",
  business: "Business",
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      if (!user) return;
      setDataLoading(true);
      try {
        const [prof, hist] = await Promise.all([
          getUserProfile(user.uid),
          getTestHistory(user.uid, 5),
        ]);
        setProfile(prof);
        setHistory(hist);
      } catch (err) {
        console.error("Error loading dashboard:", err);
      }
      setDataLoading(false);
    }
    if (user) loadData();
  }, [user]);

  const handleLogout = async () => {
    await logoutUser();
  };

  // Wait for auth to resolve
  if (authLoading) {
    return (
      <section className="pf-dash">
        <div className="pf-dash-wrap">
          <div className="pf-dash-loading">
            <div className="pf-dash-spinner" />
            <p style={{ color: "rgba(240,240,245,0.3)", fontSize: "0.85em" }}>
              Loading...
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Not logged in → redirect to login
  if (!user) return <Navigate to="/login" replace />;

  const latestTest = history[0] || null;

  return (
    <>
      <style>{CSS}</style>
      <section className="pf-dash">
        <div className="pf-dash-wrap">
          {/* Welcome */}
          <div className="pf-dash-welcome">
            <h1 className="pf-dash-hello">
              Welcome, <em>{user.displayName || "Student"}</em> 👋
            </h1>
            <button className="pf-dash-logout" onClick={handleLogout}>
              🚪 Log Out
            </button>
          </div>

          {dataLoading ? (
            <div className="pf-dash-loading">
              <div className="pf-dash-spinner" />
              <p style={{ color: "rgba(240,240,245,0.3)", fontSize: "0.85em" }}>
                Loading your data...
              </p>
            </div>
          ) : (
            <div className="pf-dash-grid">
              {/* Profile Stats */}
              <div className="pf-dash-card" style={{ animationDelay: "0.05s" }}>
                <div className="pf-dash-card-h">
                  <div
                    className="pf-dash-card-icon"
                    style={{
                      background: "rgba(255,107,53,0.1)",
                      border: "1px solid rgba(255,107,53,0.2)",
                    }}
                  >
                    👤
                  </div>
                  <div className="pf-dash-card-title">Your Profile</div>
                </div>
                <div className="pf-dash-stat-grid">
                  <div className="pf-dash-stat-item">
                    <span
                      className="pf-dash-stat-val"
                      style={{ color: "#FF6B35" }}
                    >
                      {profile?.testsCompleted || 0}
                    </span>
                    <span className="pf-dash-stat-label">Tests Taken</span>
                  </div>
                  <div className="pf-dash-stat-item">
                    <span
                      className="pf-dash-stat-val"
                      style={{ color: "#00D4AA" }}
                    >
                      {profile?.savedCareers?.length || 0}
                    </span>
                    <span className="pf-dash-stat-label">Saved Careers</span>
                  </div>
                  <div className="pf-dash-stat-item">
                    <span
                      className="pf-dash-stat-val"
                      style={{ fontSize: "1.2em" }}
                    >
                      {profile?.latestPersonalityEmoji || "❓"}
                    </span>
                    <span className="pf-dash-stat-label">Personality</span>
                  </div>
                  <div className="pf-dash-stat-item">
                    <span
                      className="pf-dash-stat-val"
                      style={{ color: "#7C5CFC", fontSize: "1em" }}
                    >
                      {user.email?.split("@")[0]}
                    </span>
                    <span className="pf-dash-stat-label">Account</span>
                  </div>
                </div>
              </div>

              {/* Latest Personality */}
              <div className="pf-dash-card" style={{ animationDelay: "0.1s" }}>
                <div className="pf-dash-card-h">
                  <div
                    className="pf-dash-card-icon"
                    style={{
                      background: "rgba(124,92,252,0.1)",
                      border: "1px solid rgba(124,92,252,0.2)",
                    }}
                  >
                    🧠
                  </div>
                  <div className="pf-dash-card-title">Your Personality</div>
                </div>
                {latestTest ? (
                  <>
                    <div className="pf-dash-persona">
                      <span className="pf-dash-persona-emoji">
                        {latestTest.personality?.emoji}
                      </span>
                      <div>
                        <div className="pf-dash-persona-title">
                          {latestTest.personality?.title}
                        </div>
                        <div className="pf-dash-persona-sub">
                          Dominant: {latestTest.personality?.dominant} ·
                          Secondary: {latestTest.personality?.secondary}
                        </div>
                      </div>
                    </div>
                    <div className="pf-dash-traits">
                      {latestTest.traits &&
                        Object.entries(latestTest.traits)
                          .sort((a, b) => b[1] - a[1])
                          .map(([trait, pct]) => (
                            <div className="pf-dash-trait" key={trait}>
                              <div className="pf-dash-trait-label">
                                {TRAIT_LABELS[trait]}
                              </div>
                              <div className="pf-dash-trait-bar">
                                <div
                                  className="pf-dash-trait-fill"
                                  style={{
                                    width: `${pct}%`,
                                    background: TRAIT_COLORS[trait],
                                  }}
                                />
                              </div>
                              <div
                                className="pf-dash-trait-pct"
                                style={{ color: TRAIT_COLORS[trait] }}
                              >
                                {pct}%
                              </div>
                            </div>
                          ))}
                    </div>
                  </>
                ) : (
                  <div className="pf-dash-empty">
                    <span className="pf-dash-empty-icon">🧭</span>
                    Take the career test to discover your personality
                    <div style={{ marginTop: 12 }}>
                      <Link
                        to="/test"
                        className="btn-primary"
                        style={{ fontSize: "0.82em", padding: "10px 22px" }}
                      >
                        Take Test
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Top Career Matches */}
              <div className="pf-dash-card" style={{ animationDelay: "0.15s" }}>
                <div className="pf-dash-card-h">
                  <div
                    className="pf-dash-card-icon"
                    style={{
                      background: "rgba(0,212,170,0.1)",
                      border: "1px solid rgba(0,212,170,0.2)",
                    }}
                  >
                    🎯
                  </div>
                  <div className="pf-dash-card-title">Top Career Matches</div>
                </div>
                {latestTest?.topCareers?.length > 0 ? (
                  latestTest.topCareers.map((c) => (
                    <Link
                      to={`/careers/${c.id}`}
                      className="pf-dash-career-row"
                      key={c.id}
                      style={{ textDecoration: "none" }}
                    >
                      <span className="pf-dash-career-emoji">{c.emoji}</span>
                      <span className="pf-dash-career-name">{c.title}</span>
                      <span
                        className="pf-dash-career-pct"
                        style={{ color: "#FF6B35" }}
                      >
                        {c.matchPercentage}%
                      </span>
                    </Link>
                  ))
                ) : (
                  <div className="pf-dash-empty">
                    <span className="pf-dash-empty-icon">🎯</span>
                    Career matches appear after taking the test
                  </div>
                )}
              </div>

              {/* Test History */}
              <div className="pf-dash-card" style={{ animationDelay: "0.2s" }}>
                <div className="pf-dash-card-h">
                  <div
                    className="pf-dash-card-icon"
                    style={{
                      background: "rgba(255,210,63,0.1)",
                      border: "1px solid rgba(255,210,63,0.2)",
                    }}
                  >
                    📜
                  </div>
                  <div className="pf-dash-card-title">Test History</div>
                </div>
                {history.length > 0 ? (
                  history.map((test) => (
                    <div className="pf-dash-history-item" key={test.id}>
                      <span className="pf-dash-history-emoji">
                        {test.personality?.emoji || "🧭"}
                      </span>
                      <div>
                        <div className="pf-dash-history-title">
                          {test.personality?.title || "Unknown"}
                        </div>
                        <div className="pf-dash-history-date">
                          {test.completedAt instanceof Date
                            ? test.completedAt.toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "Recently"}
                        </div>
                        <div className="pf-dash-history-careers">
                          {test.topCareers
                            ?.slice(0, 3)
                            .map((c) => c.emoji + " " + c.title)
                            .join(" · ")}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="pf-dash-empty">
                    <span className="pf-dash-empty-icon">📜</span>
                    Your test history will appear here
                  </div>
                )}
              </div>

              {/* CTA */}
              <div
                className="pf-dash-card full"
                style={{ animationDelay: "0.25s", textAlign: "center" }}
              >
                <h3
                  style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: "1.2em",
                    color: "#F0F0F5",
                    marginBottom: 8,
                  }}
                >
                  {latestTest
                    ? "Ready to explore more?"
                    : "Start your career discovery journey!"}
                </h3>
                <p
                  style={{
                    fontSize: "0.85em",
                    color: "rgba(240,240,245,0.3)",
                    marginBottom: 18,
                  }}
                >
                  {latestTest
                    ? "Retake the test or explore the career library."
                    : "Take the 5-minute career test to find your perfect path."}
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    to="/test"
                    className="btn-primary"
                    style={{ fontSize: "0.88em" }}
                  >
                    {latestTest ? "🔄 Retake Test" : "🚀 Take Career Test"}
                  </Link>
                  <Link
                    to="/careers"
                    className="btn-secondary"
                    style={{ fontSize: "0.88em" }}
                  >
                    📚 Career Library
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
